import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Typography, Paper, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton,
  LinearProgress, TextField, InputAdornment, Select, MenuItem,
  FormControl, InputLabel, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip, CircularProgress,
  Snackbar, Alert, Card,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { API_BASE_URL } from "../../config";
import { needsLicenceFile } from "../../utils/tenantHosting";
import { minContractEndDate, validateContractEnd } from "../../utils/contractDateValidation";

// ─── helpers ──────────────────────────────────────────────────────────────────

const CLOUD_BADGE = { aws: "#FF9900", gcp: "#4285F4", azure: "#0089D6", private: "#757575" };
const TIER_BADGE  = { tenant: "#1976d2", enterprise: "#7b1fa2", multitenant: "#00838f" };

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

function expiryColor(dateStr) {
  const d = daysUntil(dateStr);
  if (d === null) return "text.secondary";
  if (d < 0) return "error.main";
  if (d <= 30) return "warning.main";
  return "success.main";
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value }) {
  return (
    <Card elevation={0} sx={{
      height: 80, borderRadius: "12px", border: "1px solid #D9D9D9",
      display: "flex", flexDirection: "column", justifyContent: "center",
      alignItems: "center", bgcolor: "#FFFFFF",
    }}>
      <Typography fontSize="13px" color="#344054">{label}</Typography>
      <Typography fontSize="26px" fontWeight={500} mt={0.5}>{value ?? "—"}</Typography>
    </Card>
  );
}

// ─── Extend Licence Dialog ────────────────────────────────────────────────────

function ExtendLicenceDialog({ open, tenant, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (tenant) {
      setForm({
        tier: tenant.tier,
        seat_count: tenant.seat_count,
        warning_threshold: tenant.warning_threshold,
        contract_start: tenant.contract_start?.split("T")[0],
        contract_end: tenant.contract_end?.split("T")[0],
        expiry_behaviour: tenant.expiry_behaviour,
      });
    }
  }, [tenant]);

  const contractEndError = validateContractEnd(form.contract_end, form.contract_start);
  const contractEndMin = minContractEndDate(form.contract_start);

  const handleSave = async () => {
    const endError = validateContractEnd(form.contract_end, form.contract_start);
    if (endError) {
      setError(endError);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await axios.put(`${API_BASE_URL}/superadmin/tenants/${tenant.id}/licence`, form);
      const key = res.data?.licenceKey;
      if (key && needsLicenceFile(tenant)) {
        const blob = new Blob([key], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${tenant.slug || "tenant"}.lic`;
        a.click();
        URL.revokeObjectURL(url);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update licence");
    } finally {
      setSaving(false);
    }
  };

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Extend / Modify Licence — {tenant?.org_name}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Tier</InputLabel>
              <Select value={form.tier || ""} onChange={f("tier")} label="Tier">
                <MenuItem value="tenant">Tenant</MenuItem>
                <MenuItem value="enterprise">Enterprise</MenuItem>
                <MenuItem value="multitenant">Multitenant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Seat Count" type="number"
              value={form.seat_count || ""} onChange={f("seat_count")} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Warning Threshold %" type="number"
              value={form.warning_threshold || ""} onChange={f("warning_threshold")} />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>On Expiry</InputLabel>
              <Select value={form.expiry_behaviour || ""} onChange={f("expiry_behaviour")} label="On Expiry">
                <MenuItem value="lock">Lock Access</MenuItem>
                <MenuItem value="warn">Read-only mode</MenuItem>
                <MenuItem value="grace">Grace Period</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Contract Start" type="date"
              value={form.contract_start || ""} onChange={f("contract_start")}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Contract End"
              type="date"
              value={form.contract_end || ""}
              onChange={f("contract_end")}
              InputLabelProps={{ shrink: true }}
              error={Boolean(form.contract_end && contractEndError)}
              helperText={
                form.contract_end && contractEndError
                  ? contractEndError
                  : "Must be today or a future date, and on or after contract start"
              }
              inputProps={{ min: contractEndMin }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving || Boolean(contractEndError)} variant="contained"
          sx={{ bgcolor: "#0094FB", "&:hover": { bgcolor: "#0077cc" }, textTransform: "none" }}>
          {saving ? <CircularProgress size={18} /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TenantManagementPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCloud, setFilterCloud] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTier, setFilterTier] = useState("all");
  const [extendTenant, setExtendTenant] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const showSnack = (msg, severity = "success") => setSnack({ open: true, msg, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/superadmin/tenants`),
        axios.get(`${API_BASE_URL}/superadmin/stats`),
      ]);
      setTenants(tRes.data.tenants || []);
      setStats(sRes.data || {});
    } catch {
      showSnack("Failed to load tenants", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleStatus = async (tenant) => {
    const newStatus = tenant.status === "active" ? "suspended" : "active";
    try {
      await axios.put(`${API_BASE_URL}/superadmin/tenants/${tenant.id}/status`, { status: newStatus });
      showSnack(`Tenant ${newStatus}`);
      load();
    } catch {
      showSnack("Failed to update status", "error");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/superadmin/tenants/${deleteTarget.id}`);
      setDeleteTarget(null);
      showSnack("Tenant deleted");
      load();
    } catch {
      showSnack("Failed to delete tenant", "error");
    }
  };

  const handleDownloadLic = async (tenant) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/superadmin/tenants/${tenant.id}/licence-key`);
      const blob = new Blob([res.data.licenceKey], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tenant.slug}.lic`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showSnack("Failed to download licence key", "error");
    }
  };

  const filtered = tenants.filter((t) => {
    const matchSearch = !search ||
      t.org_name.toLowerCase().includes(search.toLowerCase()) ||
      t.slug.toLowerCase().includes(search.toLowerCase());
    return matchSearch &&
      (filterCloud  === "all" || t.cloud  === filterCloud) &&
      (filterStatus === "all" || t.status === filterStatus) &&
      (filterTier   === "all" || t.tier   === filterTier);
  });

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      {/* Header */}
      <Typography variant="h5" fontWeight={700}>Tenant Management</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Manage all client organisations and their Keycloak realms
      </Typography>

      {/* Actions row */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search org or slug…"
          size="small"
          sx={{ width: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
        {[
          { label: "Cloud",  value: filterCloud,  set: setFilterCloud,  opts: ["all","aws","gcp","azure","private"] },
          { label: "Status", value: filterStatus, set: setFilterStatus, opts: ["all","active","suspended","expired","pending"] },
          { label: "Tier",   value: filterTier,   set: setFilterTier,   opts: ["all","tenant","enterprise","multitenant"] },
        ].map(({ label, value, set, opts }) => (
          <FormControl key={label} size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{label}</InputLabel>
            <Select value={value} onChange={(e) => set(e.target.value)} label={label}>
              {opts.map((o) => (
                <MenuItem key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
        <IconButton onClick={load} size="small" sx={{ border: "1px solid #D9D9D9", borderRadius: 1 }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => navigate("/platform/tenants/new")}
          sx={{
            textTransform: "none", borderRadius: "10px", px: 2.5, fontWeight: 600,
            color: "#0094FB", borderColor: "#0094FB", bgcolor: "#FFFFFF",
            "&:hover": { bgcolor: "#E8F3FF", borderColor: "#0094FB" },
          }}
        >
          New Tenant
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total Tenants",       value: stats.total_tenants },
          { label: "Total Licences",      value: stats.total_licences },
          { label: "Expiring This Month", value: stats.expiring_soon },
          { label: "Expired / Suspended", value: stats.expired_suspended },
        ].map((s) => (
          <Grid key={s.label} item xs={6} md={3}>
            <StatCard label={s.label} value={s.value} />
          </Grid>
        ))}
      </Grid>

      {/* Table */}
      <TableContainer component={Paper} elevation={0}
        sx={{ border: "1px solid #e8e8e8", borderRadius: 2, bgcolor: "#FFFFFF" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f9fafb" }}>
              {["Organisation", "Cloud", "Auth", "Tier", "Licence Usage", "Expiry", "Status", "Actions"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 600, fontSize: "13px", color: "#344054", borderBottom: "1px solid #e8e8e8" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, border: "none" }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, border: "none", color: "text.secondary" }}>
                  No tenants found
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((t) => {
                const usedPct = t.seat_count > 0 ? Math.min(100, Math.round((t.seats_used / t.seat_count) * 100)) : 0;
                const overWarn = usedPct >= (t.warning_threshold || 80);
                const cloudColor = CLOUD_BADGE[t.cloud] || "#757575";
                const tierColor  = TIER_BADGE[t.tier]   || "#1976d2";

                return (
                  <TableRow key={t.id}
                    sx={{ "&:hover": { bgcolor: "#f9fafb" }, "& td": { borderBottom: "1px solid #f0f0f0" } }}>
                    <TableCell>
                      <Typography fontWeight={600} fontSize="14px">{t.org_name}</Typography>
                      <Typography fontSize="12px" color="text.secondary">{t.slug}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={t.cloud.toUpperCase()} size="small"
                        sx={{ bgcolor: cloudColor + "1A", color: cloudColor,
                          border: `1px solid ${cloudColor}44`, fontWeight: 600, fontSize: "11px" }} />
                    </TableCell>
                    <TableCell>
                      <Typography fontSize="13px" color="text.secondary">
                        {t.auth_mode === "standard" ? "Standard" : t.auth_mode.toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={t.tier.charAt(0).toUpperCase() + t.tier.slice(1)} size="small"
                        sx={{ bgcolor: tierColor + "1A", color: tierColor,
                          border: `1px solid ${tierColor}44`, fontWeight: 600, fontSize: "11px" }} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress variant="determinate" value={usedPct}
                            sx={{ height: 6, borderRadius: 3,
                              "& .MuiLinearProgress-bar": { bgcolor: overWarn ? "#ed6c02" : "#1976d2" } }} />
                        </Box>
                        <Typography fontSize="12px" color={overWarn ? "warning.main" : "text.secondary"} sx={{ minWidth: 50 }}>
                          {t.seats_used}/{t.seat_count}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize="13px" fontWeight={500} sx={{ color: expiryColor(t.contract_end) }}>
                        {formatDate(t.contract_end)}
                      </Typography>
                      {daysUntil(t.contract_end) !== null && daysUntil(t.contract_end) >= 0 && (
                        <Typography fontSize="11px" color="text.secondary">
                          {daysUntil(t.contract_end)}d left
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                        size="small"
                        color={{ active: "success", suspended: "error", expired: "warning", pending: "default" }[t.status]}
                        sx={{ fontWeight: 600, fontSize: "11px" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => navigate(`/platform/tenants/${t.id}`)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Extend Licence">
                          <IconButton size="small" onClick={() => setExtendTenant(t)}>
                            <ExtensionOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.status === "active" ? "Suspend" : "Activate"}>
                          <IconButton size="small" onClick={() => handleToggleStatus(t)}>
                            {t.status === "active"
                              ? <PauseCircleOutlinedIcon fontSize="small" sx={{ color: "#ed6c02" }} />
                              : <PlayCircleOutlinedIcon fontSize="small" sx={{ color: "#2e7d32" }} />}
                          </IconButton>
                        </Tooltip>
                        {needsLicenceFile(t) && (
                          <Tooltip title="Download .lic Key">
                            <IconButton size="small" onClick={() => handleDownloadLic(t)}>
                              <VpnKeyOutlinedIcon fontSize="small" sx={{ color: "#1976d2" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete Tenant">
                          <IconButton size="small" onClick={() => setDeleteTarget(t)}
                            sx={{ color: "#d32f2f", "&:hover": { bgcolor: "rgba(211,47,47,0.08)" } }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Extend Licence Dialog */}
      <ExtendLicenceDialog
        open={!!extendTenant}
        tenant={extendTenant}
        onClose={() => setExtendTenant(null)}
        onSaved={load}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Tenant</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently delete the Keycloak realm <strong>{deleteTarget?.slug}</strong> and all its users.
          </Alert>
          <Typography fontSize="14px">
            Are you sure you want to delete <strong>{deleteTarget?.org_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" sx={{ textTransform: "none" }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
